import { supabase } from '@/integrations/supabase/client';
import { SymbolConverter } from '@/utils/symbol-converter';

// Cache for symbols from database
let CACHED_SYMBOLS: string[] = [];
let SYMBOLS_LAST_FETCHED = 0;
const SYMBOLS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Fallback symbols for when database is unavailable
const FALLBACK_SYMBOLS = [
  // Crypto
  'BTCUSD', 'BTCUSDT', 'ETHUSD', 'ETHUSDT', 'ADAUSDT', 'SOLUSDT', 
  'BNBUSDT', 'TRXUSDT', 'DOGEUSDT', 'XRPUSDT', 'LINKUSDT', 'APTUSDT',
  'ATOMUSDT', 'AVAXUSDT', 'MATICUSDT', 'DOTUSDT', 'ICPUSDT',
  // Forex
  'EURUSD', 'GBPUSD', 'USDJPY', 'EURJPY', 'GBPJPY', 'AUDUSD', 
  'USDCAD', 'NZDUSD', 'EURGBP', 'USDCHF',
  // Commodities
  'XAUUSD', 'XAGUSD', 'GOLD', 'SILVER'
];

// In-memory cache for client-side caching (TTL: 60 seconds)
const CLIENT_CACHE = new Map<string, { price: number; source: 'cache' | 'api'; timestamp: number }>();
const CACHE_TTL = 60 * 1000; // 60 seconds

// Pending requests to prevent duplicate calls
const PENDING_REQUESTS = new Map<string, Promise<PriceResult>>();

// Rate limiting
const MAX_CONCURRENT = 4;
let activeRequests = 0;
const requestQueue: Array<() => void> = [];

export interface PriceResult {
  price: number;
  source: 'cache' | 'api';
}

export class PriceFetchService {
  /**
   * Get price with rate limiting, caching, and retry logic
   */
  static async getPrice(symbol: string): Promise<PriceResult> {
    const normalizedSymbol = symbol.toUpperCase();
    console.log(`Getting price for ${symbol} (normalized: ${normalizedSymbol}) - Active requests: ${activeRequests}`);
    
    // Check client-side cache first
    const cached = CLIENT_CACHE.get(normalizedSymbol);
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
      console.log(`Using client cache for ${symbol}: ${cached.price}`);
      return { price: cached.price, source: cached.source };
    }

    // Check if there's already a pending request for this symbol
    const existing = PENDING_REQUESTS.get(normalizedSymbol);
    if (existing) {
      console.log(`Joining existing request for ${symbol}`);
      return existing;
    }

    // Create new request promise
    const requestPromise = this.executeRequest(normalizedSymbol);
    PENDING_REQUESTS.set(normalizedSymbol, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result
      CLIENT_CACHE.set(normalizedSymbol, {
        price: result.price,
        source: result.source,
        timestamp: Date.now()
      });
      
      return result;
    } finally {
      PENDING_REQUESTS.delete(normalizedSymbol);
    }
  }

  /**
   * Execute the actual price request with rate limiting and retries
   */
  private static async executeRequest(symbol: string): Promise<PriceResult> {
    // Wait for available slot
    await this.waitForSlot();
    
    activeRequests++;
    try {
      return await this.executeRequestWithRetry(symbol);
    } finally {
      activeRequests--;
      this.processQueue();
    }
  }

  /**
   * Execute request with retry logic for 429 errors
   */
  private static async executeRequestWithRetry(symbol: string, attempt = 1): Promise<PriceResult> {
    try {
      // Check DB cache first
      const cachedPrice = await this.getCachedPrice(symbol);
      if (cachedPrice !== null) {
        console.log(`Using DB cache for ${symbol}: ${cachedPrice}`);
        return { price: cachedPrice, source: 'cache' };
      }

      // Call edge function for fresh price
      console.log(`Calling edge function for ${symbol} (attempt ${attempt})`);
      const { data, error } = await supabase.functions.invoke('fetch-price', {
        body: { symbol }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message);
      }

      if (data?.error) {
        // Handle rate limiting with retry
        if (data.error.includes('run out of API credits') && attempt <= 3) {
          const delay = attempt === 1 ? 15000 : attempt === 2 ? 30000 : 60000;
          console.log(`Rate limited for ${symbol}, retrying in ${delay/1000}s (attempt ${attempt}/3)`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.executeRequestWithRetry(symbol, attempt + 1);
        }
        
        console.error('Edge function returned error:', data.error);
        throw new Error(data.error);
      }

      if (!data || typeof data.price !== 'number') {
        console.error('Invalid response from edge function:', data);
        throw new Error('Invalid price data received');
      }

      console.log(`Successfully fetched price for ${symbol}: ${data.price} (source: ${data.source})`);
      return { 
        price: data.price, 
        source: data.source === 'cache' ? 'cache' : 'api'
      };
    } catch (error) {
      console.error(`Error fetching price for ${symbol} (attempt ${attempt}):`, error);
      
      // Don't retry on non-rate-limit errors
      if (!error.message.includes('run out of API credits')) {
        throw new Error(`Could not fetch price for ${symbol}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      
      // Re-throw rate limit errors if max attempts reached
      throw error;
    }
  }

  /**
   * Wait for available request slot
   */
  private static async waitForSlot(): Promise<void> {
    if (activeRequests < MAX_CONCURRENT) {
      return;
    }

    return new Promise<void>((resolve) => {
      requestQueue.push(resolve);
    });
  }

  /**
   * Process queued requests
   */
  private static processQueue(): void {
    if (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT) {
      const next = requestQueue.shift();
      if (next) next();
    }
  }

  /**
   * Load symbols from database
   */
  private static async loadSymbolsFromDatabase(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('position_mappings')
        .select('symbol')
        .eq('is_active', true);

      if (error) {
        console.warn('Failed to load symbols from database:', error);
        return FALLBACK_SYMBOLS;
      }

      const symbols = data?.map(row => row.symbol.toUpperCase()) || [];
      return symbols.length > 0 ? symbols : FALLBACK_SYMBOLS;
    } catch (error) {
      console.warn('Error loading symbols from database:', error);
      return FALLBACK_SYMBOLS;
    }
  }

  /**
   * Get supported symbols (cached from database)
   */
  static async getSupportedSymbols(): Promise<string[]> {
    const now = Date.now();
    
    // Return cached symbols if still valid
    if (CACHED_SYMBOLS.length > 0 && (now - SYMBOLS_LAST_FETCHED) < SYMBOLS_CACHE_TTL) {
      return [...CACHED_SYMBOLS];
    }

    // Load fresh symbols from database
    CACHED_SYMBOLS = await this.loadSymbolsFromDatabase();
    SYMBOLS_LAST_FETCHED = now;
    
    return [...CACHED_SYMBOLS];
  }

  /**
   * Check if symbol is supported
   */
  static async isSymbolSupported(symbol: string): Promise<boolean> {
    const supportedSymbols = await this.getSupportedSymbols();
    return supportedSymbols.includes(symbol.toUpperCase());
  }

  /**
   * Get cross rate for currency conversion (e.g., GBP/USD for GBPJPY)
   */
  static async getCrossRate(baseCurrency: string): Promise<number> {
    const crossSymbol = `${baseCurrency}USD`;
    console.log(`Getting cross rate for ${baseCurrency}: ${crossSymbol}`);
    
    const result = await this.getPrice(crossSymbol);
    return result.price;
  }

  /**
   * Get cached price if available and not expired
   */
  private static async getCachedPrice(symbol: string): Promise<number | null> {
    try {
      const { data } = await supabase
        .from('csv_analysis_price_cache')
        .select('price')
        .eq('symbol', symbol)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      return data?.price || null;
    } catch (error) {
      console.warn('Cache check failed:', error);
      return null;
    }
  }
}